
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
    X,
    Plus,
    Save,
} from "lucide-react";
import { Controller } from "react-hook-form";

import AvailabilityProfileSkeleton from "./skeletons/AvailabilityProfileSkeleton.tsx";
import { WeeklySchedule, useAvailabilityProfileData } from "@/lib/profile/availabilityProfileData";

export default function AvailabilityProfileTab() {
    const {
        isDarkMode,
        isUserWeekAvailabilityLoading,
        dayNames,
        control,
        errors,
        isDirty,
        isValid,
        watch,
        handleSubmit,
        onSubmit,
        handleToggleDay,
        handleAddSlot,
        handleUpdateSlot,
        handleRemoveSlot,
    } = useAvailabilityProfileData();

    if (isUserWeekAvailabilityLoading) return <AvailabilityProfileSkeleton />;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
                <h3
                    className={`text-lg font-semibold mb-4 transition-colors duration-300 ${isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                >
                    Working Hours
                </h3>
                <p
                    className={`text-sm mb-6 transition-colors duration-300 ${isDarkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                >
                    Set your available working hours for each day of the week
                </p>

                <div className="space-y-4">
                    {dayNames?.map(({ key, name }) => {
                        const day = key as keyof WeeklySchedule;
                        const dayValue = watch(day);
                        return (
                            <div
                                key={key}
                                className={`border rounded-lg p-4 transition-colors duration-300 ${isDarkMode ? "border-gray-700" : "border-gray-200"
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <Controller
                                            control={control}
                                            name={`${key}.enabled` as keyof WeeklySchedule & string}
                                            render={({ field }) => (
                                                <Switch checked={field.value} onCheckedChange={(checked) => {
                                                    field.onChange(checked);
                                                    handleToggleDay(key as keyof WeeklySchedule, checked);
                                                }} />
                                            )}
                                        />
                                        <Label
                                            className={`font-medium transition-colors duration-300 ${isDarkMode ? "text-gray-200" : "text-gray-900"
                                                }`}
                                        >
                                            {name}
                                        </Label>
                                    </div>
                                    {dayValue?.enabled && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleAddSlot(key as keyof WeeklySchedule)}
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            Add Time
                                        </Button>
                                    )}
                                </div>

                                {dayValue?.enabled && (
                                    <div className="space-y-2">
                                        {dayValue?.slots?.map(
                                            (slot, slotIndex) => (
                                                <div key={slotIndex} className="flex items-center gap-2">
                                                    <Controller
                                                        control={control}
                                                        name={`${day}.slots.${slotIndex}.startTime`}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="time"
                                                                className="w-32"
                                                                value={field.value}
                                                                onChange={(e) =>
                                                                    handleUpdateSlot(day, slotIndex, "startTime", e.target.value)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    <span className="text-gray-500">to</span>
                                                    <Controller
                                                        control={control}
                                                        name={`${day}.slots.${slotIndex}.endTime`}
                                                        render={({ field }) => (
                                                            <Input
                                                                type="time"
                                                                className="w-32"
                                                                value={field.value}
                                                                onChange={(e) =>
                                                                    handleUpdateSlot(day, slotIndex, "endTime", e.target.value)
                                                                }
                                                            />
                                                        )}
                                                    />
                                                    {dayValue?.slots.length > 1 && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveSlot(day, slotIndex)}
                                                            className="text-red-500 hover:text-red-700"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {errors?.[day]?.slots?.[slotIndex]?.startTime?.message && (
                                                        <p className="text-sm text-red-500 mt-2">
                                                            {String(errors?.[day]?.slots?.[slotIndex]?.startTime?.message)}
                                                        </p>
                                                    )}
                                                    {errors?.[day]?.slots?.[slotIndex]?.endTime?.message && (
                                                        <p className="text-sm text-red-500 mt-2">
                                                            {String(errors?.[day]?.slots?.[slotIndex]?.endTime?.message)}
                                                        </p>
                                                    )}
                                                </div>
                                            ),
                                        )}
                                        {errors?.[day]?.slots?.root?.message && (
                                            <p className="text-sm text-red-500 mt-1">
                                                {String(errors[day].slots?.root?.message)}
                                            </p>
                                        )}
                                        {errors?.[day]?.slots?.message && (
                                            <p className="text-sm text-red-500 mt-1">
                                                {String(errors[day].slots?.message)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                    type="submit"
                    disabled={!isDirty || !isValid || isUserWeekAvailabilityLoading}
                    className={`bg-slate-900 hover:bg-slate-800 ${!isDirty || !isValid ? "opacity-50 pointer-events-none" : ""
                        }`}
                >
                    <Save className="w-4 h-4 mr-2" />
                    {isUserWeekAvailabilityLoading ? "Saving..." : "Save Changes"}
                </Button>
            </div>
        </form>
    )
}
